import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Eye, EyeOff, ShieldCheck, TrendingUp, Landmark } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { motion } from "framer-motion";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.8-6.8C35.6 2.4 30.2 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.9 6.2C12.2 13.3 17.6 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.2-.4-4.6H24v9.1h12.4c-.5 2.9-2.1 5.3-4.6 7l7.1 5.5c4.2-3.9 6.6-9.6 6.6-16.9z" />
      <path fill="#FBBC05" d="M10.4 28.5c-.5-1.4-.8-3-.8-4.5s.3-3.1.8-4.5l-7.9-6.2C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.7l7.9-6.2z" />
      <path fill="#34A853" d="M24 48c6.2 0 11.5-2 15.3-5.5l-7.1-5.5c-2 1.4-4.6 2.2-8.2 2.2-6.4 0-11.8-3.8-13.6-9.2l-7.9 6.2C6.4 42.6 14.6 48 24 48z" />
    </svg>
  );
}
function AppleIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 384 512" aria-hidden><path fill="#000" d="M318.7 268c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.6-44.6-35.4-2.8-74.1 20.7-88.3 20.7-15 0-49.2-19.7-75.5-19.7C61.2 141.2 8 184.8 8 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.6 107.2 125.1 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-92.5zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" /></svg>
  );
}

const PROOF = [
  { icon: ShieldCheck, text: "Real-time credit & funding readiness — never fabricated" },
  { icon: TrendingUp, text: "Build business tradelines that actually report" },
  { icon: Landmark, text: "Explore matched capital where it's genuinely available" },
];

export default function Login() {
  const { login, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    setError("");
    if (!email || !password) return;
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.role === "admin" ? "/owner" : "/home");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const social = (p) => toast.info(`${p} sign-in is coming soon — use email & password or Google for now.`);
  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/adaptive";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const inputCls = (val) =>
    `w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] outline-none transition-colors focus:border-[#5B4BE6] focus:ring-2 focus:ring-[#5B4BE6]/15 ${touched && !val ? "border-red-400" : "border-slate-300"}`;

  return (
    <div className="font-inter flex min-h-screen bg-white" data-testid="login-page">
      {/* Left — slate brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B1020] p-12 text-white lg:flex xl:p-16">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#5B4BE6]/25 via-transparent to-transparent" />
        <Link to="/" className="relative" data-testid="login-brand-logo"><Logo variant="light" height={30} /></Link>
        <div className="relative">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A5B4FC]"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Millionaire Adaptive OS</motion.div>
          <h2 className="font-head text-4xl font-extrabold leading-[1.05] tracking-tight xl:text-[46px]">Your capital<br />command center.</h2>
          <div className="relative mt-8 h-8" data-testid="login-trajectory">
            <div className="absolute left-0 right-0 top-[9px] h-px bg-white/10" />
            <motion.div className="absolute left-0 top-[8px] h-[3px] rounded-full bg-gradient-to-r from-[#7C6BFF] to-[#5B4BE6]" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 1.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }} />
            <div className="absolute inset-x-0 top-0 flex justify-between">
              {["Account", "Business", "Credit file", "Bank", "Readiness", "Capital"].map((l, i) => (
                <motion.div key={l} className="flex flex-col items-center" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.7 + i * 0.22, duration: 0.4 }}>
                  <span className="h-[19px] w-[19px] rounded-full border-2 border-[#5B4BE6] bg-[#5B4BE6]" />
                  <span className="mt-2 font-mono text-[9px] uppercase tracking-wider text-[#A5B4FC]">{l}</span>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="mt-10 space-y-4">
            {PROOF.map((p, i) => (
              <motion.div key={p.text} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9 + i * 0.15, duration: 0.5 }} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5"><p.icon size={15} className="text-[#A5B4FC]" /></span>
                <span className="text-[14px] leading-snug text-slate-300">{p.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="relative font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">People. Plan. Opportunity.</div>
      </div>

      {/* Right — form */}
      <div className="flex w-full flex-col items-center justify-center px-4 py-14 lg:w-1/2">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block lg:hidden"><Logo variant="dark" height={34} /></Link>
          <h1 className="mt-7 font-head text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:mt-0">Welcome back</h1>
          <p className="mt-2 text-[15px] text-slate-600">
            Don't have an account? <Link to="/register" className="font-semibold text-[#5B4BE6] underline" data-testid="go-register-link">Create an account</Link>
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                data-testid="login-email-input" className={inputCls(email)} />
              {touched && !email && <div className="mt-1 text-[13px] text-red-500">Required</div>}
            </div>
            <div>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
                  data-testid="login-password-input" className={inputCls(password) + " pr-12"} />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="toggle-password"
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#5B4BE6]">
                  {showPw ? <EyeOff size={19} /> : <Eye size={19} />}
                </button>
              </div>
              {touched && !password && <div className="mt-1 text-[13px] text-red-500">Required</div>}
            </div>

            <label className="flex cursor-pointer items-center gap-2.5 text-[14px] text-slate-700">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-5 w-5 rounded border-slate-300 accent-[#5B4BE6]" />
              Remember my email
            </label>

            {error && <div data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2 text-[14px] text-red-600">{error}</div>}

            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#5B4BE6] py-3.5 text-[16px] font-semibold text-white shadow-[0_16px_40px_-18px_rgba(91,75,230,0.8)] transition-all hover:-translate-y-0.5 hover:bg-[#4F46E5] disabled:opacity-60">
              {loading ? <Loader2 size={18} className="animate-spin" /> : "Log in"}
            </button>

            <div className="text-center">
              <Link to="/register" className="text-[14px] font-medium text-slate-500 underline hover:text-slate-700">Forgot your password?</Link>
            </div>

            <div className="flex items-center gap-4 py-1"><div className="h-px flex-1 bg-slate-200" /><span className="text-[13px] text-slate-400">or</span><div className="h-px flex-1 bg-slate-200" /></div>

            <button type="button" onClick={googleLogin} data-testid="google-signin"
              className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-slate-300 bg-white py-3.5 text-[15px] font-semibold text-slate-800 transition-colors hover:border-[#5B4BE6] hover:bg-slate-50">
              <GoogleIcon /> Sign in with Google
            </button>
            <button type="button" onClick={() => social("Apple")} data-testid="apple-signin"
              className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-slate-200 bg-white py-3.5 text-[15px] font-semibold text-slate-400 opacity-80 transition-colors hover:bg-slate-50">
              <AppleIcon /> Sign in with Apple
              <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-slate-400">Soon</span>
            </button>
          </form>

          <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-[12px] text-slate-500">
            Demo member: <span className="text-slate-800">demo@mmt.com</span> / <span className="text-slate-800">demo123</span>
          </div>
        </div>
      </div>
    </div>
  );
}
